import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAvailableContacts } from '@/hooks/useAvailableContacts';
import type { PortalUser } from '@/hooks/usePortalUsers';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const portalUserSchema = z.object({
  contact_id: z.string().uuid('Selecteer een contact'),
  role: z.enum(['inspector', 'client'], {
    required_error: 'Selecteer een rol',
  }),
  password: z.string().min(8, 'Wachtwoord moet minimaal 8 tekens bevatten'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Wachtwoorden komen niet overeen',
  path: ['confirmPassword'],
});

type PortalUserFormData = z.infer<typeof portalUserSchema>;

interface PortalUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: PortalUser | null;
  onSuccess: () => void;
}

export function PortalUserModal({
  open,
  onOpenChange,
  user,
  onSuccess,
}: PortalUserModalProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [contactSearchOpen, setContactSearchOpen] = useState(false);
  const isEditing = !!user;
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<PortalUserFormData>({
    resolver: zodResolver(portalUserSchema),
    defaultValues: {
      contact_id: '',
      role: 'inspector',
      password: '',
      confirmPassword: '',
    },
  });

  const selectedRole = watch('role');
  const selectedContactId = watch('contact_id');
  
  const { data: availableContacts = [], isLoading: contactsLoading } = useAvailableContacts(selectedRole);

  useEffect(() => {
    if (!open) {
      reset({
        contact_id: '',
        role: 'inspector',
        password: '',
        confirmPassword: '',
      });
    }
  }, [open, reset]);

  const selectedContact = availableContacts.find(c => c.id === selectedContactId);

  const onSubmit = async (data: PortalUserFormData) => {
    setIsLoading(true);
    try {
      // Call edge function to create portal user
      const { data: result, error } = await supabase.functions.invoke('create-portal-user', {
        body: {
          contact_id: data.contact_id,
          role: data.role,
          password: data.password,
        },
      });

      if (error) throw error;
      if (!result.success) throw new Error(result.error || 'Fout bij aanmaken portal gebruiker');

      toast({
        title: 'Portal gebruiker aangemaakt',
        description: `Account aangemaakt voor ${result.email}`,
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error creating portal user:', error);
      toast({
        title: 'Fout',
        description: error.message || 'Kon portal gebruiker niet aanmaken',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nieuwe Portal Gebruiker</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="role">Rol</Label>
            <Select
              value={selectedRole}
              onValueChange={(value) => {
                setValue('role', value as 'inspector' | 'client');
                setValue('contact_id', ''); // Reset contact selection when role changes
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer een rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inspector">Inspecteur</SelectItem>
                <SelectItem value="client">Klant</SelectItem>
              </SelectContent>
            </Select>
            {errors.role && (
              <p className="text-sm text-destructive">{errors.role.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Contact</Label>
            <Popover open={contactSearchOpen} onOpenChange={setContactSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={contactSearchOpen}
                  className="w-full justify-between"
                  disabled={contactsLoading}
                >
                  {selectedContact
                    ? `${selectedContact.first_name || ''} ${selectedContact.last_name || ''} (${selectedContact.email}) - ${selectedContact.org_name || 'Geen org'}`
                    : contactsLoading
                    ? 'Laden...'
                    : 'Selecteer contact...'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-full p-0 z-[10020] bg-popover" 
                align="start"
                side="bottom"
                collisionBoundary={dialogContentRef.current ? [dialogContentRef.current] : undefined}
              >
                <Command>
                  <CommandInput placeholder="Zoek contact..." />
                  <CommandList>
                    <CommandEmpty>
                      {availableContacts.length === 0
                        ? `Geen beschikbare contacten voor ${selectedRole === 'inspector' ? 'leveranciers' : 'klanten'}`
                        : 'Geen resultaten gevonden'}
                    </CommandEmpty>
                    <CommandGroup className="max-h-64 overflow-auto">
                      {availableContacts.map((contact) => (
                        <CommandItem
                          key={contact.id}
                          value={`${String(contact.first_name ?? '')} ${String(contact.last_name ?? '')} ${String(contact.email ?? '')}`.trim()}
                          onSelect={() => {
                            if (!contact?.id) return;
                            if (selectedContactId !== contact.id) {
                              setValue('contact_id', contact.id, { shouldValidate: true, shouldDirty: true });
                            }
                            // Defer closing to avoid Popover/Dialog unmount race conditions
                            setTimeout(() => setContactSearchOpen(false), 0);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedContactId === contact.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {contact.first_name || ''} {contact.last_name || ''} ({contact.email})
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {contact.org_name || 'Geen organisatie'}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {errors.contact_id && (
              <p className="text-sm text-destructive">{errors.contact_id.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Wachtwoord</Label>
            <Input
              id="password"
              type="password"
              placeholder="Minimaal 8 tekens"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Bevestig Wachtwoord</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Herhaal wachtwoord"
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="bg-muted p-4 rounded-lg space-y-2">
            <p className="text-sm font-medium">Let op:</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Alleen contacten van <strong>{selectedRole === 'inspector' ? 'leveranciers' : 'klanten'}</strong> worden getoond</li>
              <li>Contacten die al een portal account hebben worden niet getoond</li>
              <li>Geef het wachtwoord handmatig door aan de gebruiker</li>
            </ul>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Annuleren
            </Button>
            <Button type="submit" disabled={isLoading || !selectedContactId}>
              {isLoading ? 'Bezig...' : 'Aanmaken'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
